import { postAdminAccountSwitchTokenRefreshApi } from '@/shared/apis/admin/post-admin-account-switch-token-refresh';
import {
  getRefreshTokenExpiry,
  tokenRefresh,
} from '@/shared/apis/auth/token-refresh';
import {
  isDefinitiveSwitchTokenRefreshFailure,
  SwitchSessionTerminatedError,
  SwitchTokenRefreshRaceError,
} from '@/shared/apis/auth/switch-session-error';
import {
  clearSwitchSession,
  adoptAccountSwitchSessionLocally,
  getAccountSwitchSessionEpoch,
  type AccountSwitchSessionLockContext,
  withAccountSwitchSessionLock,
} from '@/shared/lib/account-switch-session';
import {
  canCommitSwitchRefresh,
  decideSwitchRefreshFailure,
} from '@/shared/lib/account-switch-session-policy';
import { createTokenCookie, getCookie } from '@/shared/server-actions';
import { useAuthStore } from '@/shared/store';
import { useTokenStore } from '@/shared/store/token-store';
import { getTokenSessionFingerprint } from '@/shared/utils/access-token';
import { isOriginalSessionExpiredError } from '@/shared/apis/auth/account-switch-end-policy';

const MAX_REFRESH_TOKEN_RACE_RETRIES = 1;
const FALLBACK_ROTATION_PROBE_INTERVAL_MS = 50;
const FALLBACK_ROTATION_PROBE_COUNT = 3;

let inFlightSwitchTokenRefresh: Promise<string | void> | null = null;

export { isDefinitiveSwitchTokenRefreshFailure } from './switch-session-error';

async function waitForConcurrentTokenRotation(
  attemptedRefreshToken: string,
): Promise<string | null> {
  for (
    let probeCount = 0;
    probeCount < FALLBACK_ROTATION_PROBE_COUNT;
    probeCount += 1
  ) {
    await new Promise((resolve) =>
      setTimeout(resolve, FALLBACK_ROTATION_PROBE_INTERVAL_MS),
    );
    const latestRefreshToken = await getCookie('s_r_t');
    if (latestRefreshToken !== attemptedRefreshToken) {
      return latestRefreshToken;
    }
  }

  return attemptedRefreshToken;
}

async function refreshLatestSwitchToken({
  isLocked,
}: AccountSwitchSessionLockContext): Promise<string | void> {
  let currentSwitchRefreshToken = await getCookie('s_r_t');

  if (!currentSwitchRefreshToken) {
    if (useTokenStore.getState().switchAccessToken) {
      await clearSwitchSession();
      throw new SwitchSessionTerminatedError();
    }
    return;
  }

  const isRememberMe = useAuthStore.getState().isRememberMe;

  for (
    let raceRetryCount = 0;
    raceRetryCount <= MAX_REFRESH_TOKEN_RACE_RETRIES;
    raceRetryCount += 1
  ) {
    const attemptedRefreshToken: string = currentSwitchRefreshToken;
    const startEpoch = getAccountSwitchSessionEpoch();
    const attemptedSessionFingerprint = getTokenSessionFingerprint(
      attemptedRefreshToken,
    );

    try {
      const { data } = await postAdminAccountSwitchTokenRefreshApi(
        attemptedRefreshToken,
        {
          refreshOriginalAuthorization: async () => {
            const accessToken = await tokenRefresh();
            if (!accessToken) {
              throw new Error('Failed to refresh the original access token.');
            }
            return accessToken;
          },
        },
      );
      const { accessToken, refreshToken } = data;
      const latestRefreshToken = await getCookie('s_r_t');

      if (
        !canCommitSwitchRefresh({
          startEpoch,
          currentEpoch: getAccountSwitchSessionEpoch(),
          attemptedRefreshToken,
          currentRefreshToken: latestRefreshToken,
        })
      ) {
        if (
          latestRefreshToken &&
          latestRefreshToken !== attemptedRefreshToken &&
          raceRetryCount < MAX_REFRESH_TOKEN_RACE_RETRIES
        ) {
          currentSwitchRefreshToken = latestRefreshToken;
          continue;
        }

        throw latestRefreshToken
          ? new SwitchTokenRefreshRaceError()
          : new SwitchSessionTerminatedError();
      }

      await createTokenCookie({
        name: 's_r_t',
        value: refreshToken,
        maxAge: getRefreshTokenExpiry(isRememberMe),
      });

      // Web Locks 미지원 환경에서 end가 cookie 쓰기와 겹치면 stale rotation을 되돌린다.
      if (
        !canCommitSwitchRefresh({
          startEpoch,
          currentEpoch: getAccountSwitchSessionEpoch(),
          attemptedRefreshToken: refreshToken,
          currentRefreshToken: await getCookie('s_r_t'),
        })
      ) {
        // no-lock fallback에서는 check->delete도 TOCTOU다. 공유 cookie를 지우지 않고
        // 메모리 commit만 거부하여 새 세션/winner token 훼손을 피한다.
        throw new SwitchSessionTerminatedError();
      }

      if (
        getTokenSessionFingerprint(
          useTokenStore.getState().switchAccessToken,
        ) !== attemptedSessionFingerprint
      ) {
        // BroadcastChannel 미지원/누락 탭이 cookie만 가진 경우에도 cache/space를 전환한다.
        await adoptAccountSwitchSessionLocally(accessToken);
      } else {
        useTokenStore.getState().setSwitchAccessToken(accessToken);
      }
      return accessToken;
    } catch (error) {
      if (isOriginalSessionExpiredError(error)) {
        await clearSwitchSession();
        throw new SwitchSessionTerminatedError(error);
      }
      let latestRefreshToken = await getCookie('s_r_t');

      // Web Locks 미지원 브라우저에서는 loser INVALID가 winner의 cookie 회전보다
      // 먼저 도착할 수 있다. 첫 INVALID에서 바로 지우지 않고 짧게 bounded probe한다.
      if (
        latestRefreshToken === attemptedRefreshToken &&
        isDefinitiveSwitchTokenRefreshFailure(error) &&
        !(error instanceof SwitchSessionTerminatedError)
      ) {
        latestRefreshToken = await waitForConcurrentTokenRotation(
          attemptedRefreshToken,
        );
      }

      const failureDecision = decideSwitchRefreshFailure({
        attemptedRefreshToken,
        latestRefreshToken,
        isDefinitive: isDefinitiveSwitchTokenRefreshFailure(error),
        supportsLock: isLocked,
        canRetry: raceRetryCount < MAX_REFRESH_TOKEN_RACE_RETRIES,
      });
      if (failureDecision === 'retry-latest' && latestRefreshToken) {
        currentSwitchRefreshToken = latestRefreshToken;
        continue;
      }
      if (failureDecision === 'preserve') {
        // winner/new session cookie를 삭제하지 않고 outer cleanup도 막는 retryable ambiguity.
        throw new SwitchTokenRefreshRaceError();
      }
      if (failureDecision === 'clear') {
        const didClear = await clearSwitchSession({
          expectedRefreshToken: attemptedRefreshToken,
          ...(attemptedSessionFingerprint && {
            expectedSessionFingerprint: attemptedSessionFingerprint,
          }),
        });
        if (didClear) {
          throw new SwitchSessionTerminatedError(error);
        }
        // cancel/revalidation 사이 새 START가 들어오면 terminal old response를 폐기한다.
        throw new SwitchTokenRefreshRaceError();
      }

      throw error;
    }
  }

  throw new SwitchTokenRefreshRaceError();
}

/**
 * switch token을 origin-wide lock 안에서 갱신한다.
 * 자체 in-flight promise는 같은 탭의 중복 호출만 합치며, 완료 즉시 반드시 비운다.
 */
export const switchTokenRefresh = (): Promise<string | void> => {
  if (inFlightSwitchTokenRefresh) return inFlightSwitchTokenRefresh;

  const refreshPromise = withAccountSwitchSessionLock(
    refreshLatestSwitchToken,
  ).finally(() => {
    if (inFlightSwitchTokenRefresh === refreshPromise) {
      inFlightSwitchTokenRefresh = null;
    }
  });
  inFlightSwitchTokenRefresh = refreshPromise;

  return refreshPromise;
};
